import React, { useEffect, useContext, useState, useRef } from 'react';
import { Form, Formik } from 'formik';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { Tabs, Tab, Toolbar, colors, Button, Container, Grid, AppBar, Typography, Hidden, Divider, CircularProgress } from '@material-ui/core';
import { RequestHeader, RequestStepper, RequestAlerts, OpenerSelector, NoteForm, DataContainer } from '.'
import { BankInfoProvider, BankInfoTab } from './bankInfo';
import { IdentificationProvider, IdentificationTab } from './identification';
import { BusinessInfoProvider, BusinessInfoTab } from './business';
import { LogisticsProvider, LogisticsTab } from './logistics';
import { AdministrativeProvider, AdministrativeTab } from './administrative';
import { PricingProvider, PricingTab } from './pricing';
import { ContractProvider, ContractTab } from './contract';
import { AccountReceivableProvider, AccountReceivableTab } from './account'
import { CustomerRequestContext } from '../CustomerRequestContext';
import { TabsProvider } from './TabsContext';
import { NavContext, NavRoutes, UserContext } from '../../../contexts';
import { RequestStatus, ApplicationRoles, SapTableNames } from '../../../../helpers';
import { defaultRiskEvaluation, defaultEthicsCompliance, defualtLogisticDoc, defualtLogisticCuSrv, defualtLogisticPlan, defaultAddress, defaultOrder, defaultPayer, defaultBilling, defaultMargins, defaultAccountReceivable, defaultInvoiceAddress, defaultCreditManagement, defaultPricingUnit } from '../../../../content';
import SaveIcon from '@material-ui/icons/Save';
import SendIcon from '@material-ui/icons/Send';
import ThumbUpIcon from '@material-ui/icons/ThumbUp';
import ThumbDownIcon from '@material-ui/icons/ThumbDown';
import LockIcon from '@material-ui/icons/LockOutlined';
import UnlockIcon from '@material-ui/icons/LockOpenOutlined';
import CancelIcon from '@material-ui/icons/ClearOutlined';
import { makeStyles } from '@material-ui/core/styles';
import { DbType } from '../../../../helpers/Common';

const useStyles = makeStyles((theme) => {
    return (
        {
            root: {
            },
            tabs: {
                [theme.breakpoints.down('md')]: {
                    width: window.innerWidth - 48
                },
                [theme.breakpoints.down('xs')]: {
                    width: window.innerWidth - 16
                },
            },
            divider: {
                backgroundColor: colors.grey[300]
            },
            alert: {
                marginTop: 3
            },
            content: {
                marginTop: 3
            },
            appBar: {
                top: 'auto',
                bottom: 0,
            },
            cancelButton: {
                marginBottom: 10
            },
            overlay: {
                zIndex: 1
            },
            progress: {
                color: "black"
            },
            tabAsterisk: {
                color: "#ff0000"
            }
        }
    )
});

export const TabsContainer = (props) => {
    const classes: any = useStyles();
    const formRef: React.MutableRefObject<any> = useRef();

    const {
        navigateTo
    } = useContext(NavContext);

    const {
        userInfo,
        isUserInRole,
        isUserInRoles,
        draftRequests,
        setDraftRequests,
    } = useContext(UserContext);

    const {
        state,
        getTableValues,
        isSavingRequest,
        saveDraftRequest,
        updateCreditRiskInfo,
        blockRequest,
        unblockRequest,
        closeRequest,
        updateSignedContractInfo,
        updateLogisticInfo,
        updateAccountReceivableInfo,
        sendRequestToCreditRisk,
        sendRequestToLogistic,
        updatePricingInfo,
        sendRequestToCRE,
        sendRequestToARFromCR,
        reSendRequestToSap,
        sendRequestToReview,
        rejectRequest,
        clearError
    } = useContext(CustomerRequestContext);

    const {
        noSendToSap,
        errors
    } = state;

    const [currentTab, setCurrentTab] = useState("identification");
    const [isNoteOpen, setIsNoteOpen] = useState(false);
    const [submitAction, setSubmitAction] = useState(null);
    const [isNoteObligatory, setNoteObligatory] = useState(false);
    const [loadingButton, setLoadingButton] = useState(null);
    const [identificationError, setIdentificationError] = useState(null);
    const [bankError, setBankError] = useState(null);
    const [businessError, setBusinessError] = useState(null);
    const [logisticsError, setLogisticsError] = useState(null);
    const [administrativeError, setAdministrativeError] = useState(null);
    const [pricingError, setPricingError] = useState(null);
    const [accountReceivableError, setAccountReceivableError] = useState(null);
    const [isToBlock, setIsToBlock] = useState(false);

    const allTabs = [
        { value: "identification", label: "Identification and summary", asterisk: false },
        { value: "bank", label: "Client’s bank information", asterisk: false },
        { value: "business", label: "Business information", asterisk: false },
        { value: "logistics", label: "Logistics", asterisk: false },
        { value: "administrative", label: "Administrative & sales", asterisk: false },
        { value: "pricing", label: "Pricing", asterisk: false },
    ];

    const filterTabs = () => {
        let tabs = [...allTabs];

        switch (state.request.status) {
            case RequestStatus.Logistic:
            case RequestStatus.WaitingForContract:
            case RequestStatus.WaitingToBeAddedInSap:
            case RequestStatus.Approved:
                if (
                    (
                        isUserInRole(ApplicationRoles.AOFOpener) &&
                        state.request.ownerKey === userInfo.externalKey
                    ) ||
                    isUserInRole(ApplicationRoles.Administrator)
                )
                    tabs.push({ value: "contract", label: "Contract", asterisk: false });

                if (isUserInRoles([ApplicationRoles.AccountReceivable, ApplicationRoles.Administrator]))
                    tabs.push({ value: "accountReceivable", label: "Account receivable", asterisk: false });

                break;

            case RequestStatus.AccountReceivableApproval:
                if (
                    (
                        isUserInRole(ApplicationRoles.AOFOpener) &&
                        state.request.ownerKey === userInfo.externalKey
                    ) ||
                    isUserInRole(ApplicationRoles.Administrator)
                )
                    tabs.push({ value: "contract", label: "Contract", asterisk: false });

                if (isUserInRoles([ApplicationRoles.AccountReceivable,
                    ApplicationRoles.Administrator]))
                    tabs.push({ value: "accountReceivable", label: "Account receivable", asterisk: false });

                break;

            default:
                break;
        }

        return tabs;
    }

    const getTabs = () => {
        return filterTabs().map((tab, index) => {
            if (tab.value === "identification") {
                if (identificationError)
                    tab.asterisk = true;
                else
                    tab.asterisk = false;
            }

            else if (tab.value === "bank") {
                if (bankError)
                    tab.asterisk = true;
                else
                    tab.asterisk = false;
            }

            else if (tab.value === "business") {
                if (businessError)
                    tab.asterisk = true;
                else
                    tab.asterisk = false;
            }

            else if (tab.value === "logistics") {
                if (logisticsError)
                    tab.asterisk = true;
                else
                    tab.asterisk = false;
            }

            else if (tab.value === "administrative") {
                if (administrativeError)
                    tab.asterisk = true;
                else
                    tab.asterisk = false;
            }

            else if (tab.value === "pricing") {
                if (pricingError)
                    tab.asterisk = true;
                else
                    tab.asterisk = false;
            }

            else if (tab.value === "accountReceivable") {
                if (accountReceivableError)
                    tab.asterisk = true;
                else
                    tab.asterisk = false;
            }

            return (
                <Tab
                    key={tab.value}
                    label={
                        <div>
                            {tab.label}
                            {
                                tab.asterisk &&
                                <span className={classes.tabAsterisk}> *</span>
                            }
                        </div>
                    }
                    value={tab.value}
                    {...a11yProps(index)}
                />
            );
        })
    }

    const createInitialValues = (request) => {
        const values = { ...request };

        // Ensure all ref fields are translated from null -> ""
        setNullIdsAsEmptyStrings(values);

        //ethicsCompliance isn't used inside case "RequestStatus.CreditRiskEvaluation" because old requests have this field set to null. In this way when old requests get opened in any status, the default value is set avoiding crash.
        if (values.ethicsCompliance === null)
            values.ethicsCompliance = { ...defaultEthicsCompliance };

        if (values.secondaryAddress === null)
            values.secondaryAddress = { ...defaultAddress };

        if (values.order === null)
            values.order = { ...defaultOrder };

        if (values.order.payer === null)
            values.order.payer = { ...defaultPayer };

        if (values.order.billing === null)
            values.order.billing = { ...defaultBilling };

        if (values.resaleMargins === null)
            values.resaleMargins = { ...defaultMargins };

        if (values.invoiceAddresses === null || values.invoiceAddresses.length === 0)
            values.invoiceAddresses = [{ ...defaultInvoiceAddress }];

        values.isDraftJWA = !values.isPerfumesCategory && RequestStatus.Draft === values.status;
        values.isLogisticsJWA = !values.isPerfumesCategory && RequestStatus.Logistic === values.status;

        switch (request.status){
            case RequestStatus.Draft:
                if (!values.chainId){
                    const chainValues = getTableValues(SapTableNames.Chains, values.dbType);
    
                    if (chainValues.length > 0){
                        const independentChain = chainValues.filter(x => x.externalKey === "BP_CHAIN_54_E");
            
                        if (independentChain.length > 0){
                            const defaultChain = independentChain[0].tableValueId;
                            values.chainId = defaultChain;
                        }
                    }
                }

                break;

            case RequestStatus.CreditRiskEvaluation:
                if (values.riskEvaluation === null)
                    values.riskEvaluation = { ...defaultRiskEvaluation };
                
                if (!values.creditManagement)
                    values.creditManagement = { ...defaultCreditManagement };

                break;

            case RequestStatus.Logistic:
                if (
                    isUserInRoles([ApplicationRoles.LogisticDocumentation, ApplicationRoles.LogisticCustomerService]) &&
                    values.logisticDocumentation === null
                ){
                    values.logisticDocumentation = { ...defualtLogisticDoc };

                    if (values.logisticDocumentation.incotermsId === "")
                        values.logisticDocumentation.incotermsId = values.incotermsId;
                        
                    if (values.logisticDocumentation.incotermsLocation === "")
                        values.logisticDocumentation.incotermsLocation = values.incotermsLocation;
                }

                if (
                    isUserInRole(ApplicationRoles.LogisticCustomerService) &&
                    values.logisticCustomerService === null
                )
                    values.logisticCustomerService = { ...defualtLogisticCuSrv };

                if (
                    isUserInRoles([ApplicationRoles.LogisticPlanning, ApplicationRoles.LogisticPlanningPerfumes]) &&
                    values.logisticPlanning === null
                ){
                    values.logisticPlanning = { ...defualtLogisticPlan };
                    values.logisticPlanning.acceptBackOrders = request.acceptBackOrders;

                    if (values.logisticPlanning.requestedDeliveryDateId === "")
                        values.logisticPlanning.requestedDeliveryDateId = values.deliveryDateMandatoryId;
                }

                break;

            case RequestStatus.PricingUnit:
                if (!values.pricing)
                    values.pricing = { ...defaultPricingUnit };

                break;
            
            case RequestStatus.AccountReceivableApproval:
                if (values.accountReceivable === null)
                    values.accountReceivable = { ...defaultAccountReceivable };

                if (state.tables.length > 0 && values.primaryAddress.countryId) {
                    const countries = getTableValues(SapTableNames.Countries, values.dbType);
                    const country = countries.find(x => x.tableValueId === values.primaryAddress.countryId);
                    values.isCountryCanada = country.externalKey === "CA";    
                }

                break;

            default:
                break;
        }

        return values;
    }

    // This method is used to force null->"" conversion on ref fields (eg. CountryId)
    // used in select form fields. Material will complain if you bind a null value to a select
    const setNullIdsAsEmptyStrings = (obj) => {
        const check = (obj = {}) => {
            const isObject = val => val !== null && (typeof val === 'object' || Array.isArray(val));

            Object
                .entries(obj)
                .forEach(([key, value]) => {
                    if (isObject(value))
                        check(value);
                    else if (key.endsWith("Id") && (value === null || value === undefined))
                        obj[key] = "";
                });
        };

        check(obj);
    }

    const getActionButtons = () => {
        const buttons = [];

        switch (state.request.status) {
            case RequestStatus.Draft:
                if (isUserInRole(ApplicationRoles.AOFOpener)) {
                    buttons.push(getActionButton(<SaveIcon />, "Save",
                        () => {
                            setLoadingButton("Save");
                            setSubmitAction("SaveDraft");
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                    buttons.push(getActionButton(<SendIcon />, "Save & Send",
                        () => {
                            setLoadingButton("Save & Send");
                            setSubmitAction("SendDraft");
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                }

                if (isUserInRole(ApplicationRoles.BossApprover) && !isUserInRole(ApplicationRoles.AOFOpener)
                    && state.request.customerRequestId === 0) {
                    buttons.push(getActionButton(<SaveIcon />, "Save",
                        () => {
                            setLoadingButton("Save");
                            setSubmitAction("SaveDraft");
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                }

                break;

            case RequestStatus.ToBeReviewed:
                if (isUserInRole(ApplicationRoles.AOFOpener) && state.request.ownerKey === userInfo.externalKey) {
                    buttons.push(getActionButton(<SaveIcon />, "Save",
                        () => {
                            setLoadingButton("Save");
                            setSubmitAction("SaveDraft");
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                    buttons.push(getActionButton(<SendIcon />, "Save & Send",
                        () => {
                            setLoadingButton("Save & Send");
                            setSubmitAction("SendDraft");
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                }

                break;

            case RequestStatus.BossApproval:
                if (isUserInRole(ApplicationRoles.BossApprover)) {
                    buttons.push(getActionButton(<ThumbUpIcon />, "Approve",
                        () => {
                            setLoadingButton(null);
                            setNoteObligatory(false)
                            setSubmitAction("SendToCreditRisk");
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                    buttons.push(getActionButton(<SendIcon />, "Send to review",
                        () => {
                            setLoadingButton(null);
                            setNoteObligatory(true)
                            setSubmitAction("SendToReview")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                    buttons.push(getActionButton(<ThumbDownIcon />, "Reject",
                        () => {
                            setLoadingButton(null);
                            setNoteObligatory(true)
                            setSubmitAction("Reject")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                }

                break;

            case RequestStatus.CreditRiskEvaluation:
                if (isUserInRole(ApplicationRoles.CreditRisk)) {
                    buttons.push(getActionButton(<SaveIcon />, "Save",
                        () => {
                            setLoadingButton("Save");
                            setSubmitAction("SaveCreditRiskInfo")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));

                    {
                        isToBlock ?
                        buttons.push(
                            getActionButton(
                                <LockIcon />,
                                "Block",
                                () => {
                                    setLoadingButton("Block");
                                    setSubmitAction("Block");
                                    
                                    if (formRef.current)
                                        formRef.current.handleSubmit();
                                }
                            )
                        ) :
                        buttons.push(
                            getActionButton(
                                <ThumbUpIcon />,
                                "Approve",
                                () => {
                                    setLoadingButton(null);
                                    setNoteObligatory(false)
                                    setSubmitAction("SendCreditRiskInfo")
                                    
                                    if (formRef.current)
                                        formRef.current.handleSubmit();
                                }
                            )
                        )
                    }

                    buttons.push(getActionButton(<SendIcon />, "Send to review",
                        () => {
                            setLoadingButton(null);
                            setNoteObligatory(true)
                            setSubmitAction("SendToReview")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                    buttons.push(getActionButton(<ThumbDownIcon />, "Reject",
                        () => {
                            setLoadingButton(null);
                            setNoteObligatory(true)
                            setSubmitAction("Reject")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                }

                break;

            case RequestStatus.CreditRiskApproval:
                if (isUserInRole(ApplicationRoles.CreditRiskDirector)) {
                    buttons.push(getActionButton(<ThumbUpIcon />, "Approve",
                        () => {
                            setLoadingButton(null);
                            setNoteObligatory(false)
                            setSubmitAction("SendToLogistic")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                    buttons.push(getActionButton(<SendIcon />, "Send to review",
                        () => {
                            setLoadingButton(null);
                            setNoteObligatory(true)
                            setSubmitAction("SendToReview")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                    buttons.push(getActionButton(<ThumbDownIcon />, "Reject",
                        () => {
                            setLoadingButton(null);
                            setNoteObligatory(true)
                            setSubmitAction("Reject")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));

                    break;
                }

                break;

            case RequestStatus.CreditRiskReEvaluation:
                if (isUserInRole(ApplicationRoles.CreditRisk)) {
                    buttons.push(getActionButton(<ThumbUpIcon />, "Approve",
                        () => {
                            setLoadingButton(null);
                            setNoteObligatory(false)
                            setSubmitAction("SendToARFromCR")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                    buttons.push(getActionButton(<ThumbDownIcon />, "Reject",
                        () => {
                            setLoadingButton(null);
                            setNoteObligatory(true)
                            setSubmitAction("Reject")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                }

                break;

            case RequestStatus.Logistic:
                if (
                    (
                        !state.request.isLogisticDocumentationReady &&
                        isUserInRole(ApplicationRoles.LogisticDocumentation)
                    ) ||
                    (
                        !state.request.isLogisticCustomerServiceReady &&
                        isUserInRole(ApplicationRoles.LogisticCustomerService)
                    ) ||
                    (
                        !state.request.isLogisticPlanningReady &&
                        isUserInRoles([ApplicationRoles.LogisticPlanning, ApplicationRoles.LogisticPlanningPerfumes])
                    )
                ) {
                    buttons.push(
                        getActionButton(
                            <SaveIcon />,
                            "Save",
                            () => {
                                setLoadingButton("Save")
                                setSubmitAction("SaveLogisticInfo")
                                if (formRef.current)
                                    formRef.current.handleSubmit();
                            }
                        )
                    );

                    buttons.push(
                        getActionButton(
                            <SendIcon />,
                            "Save & Send",
                            () => {
                                setLoadingButton("Save & Send")
                                setSubmitAction("SendLogisticInfo")
                                if (formRef.current)
                                    formRef.current.handleSubmit();
                            }
                        )
                    );
                }

                if (isUserInRole(ApplicationRoles.AOFOpener)
                    && state.request.ownerKey === userInfo.externalKey
                    && !state.request.isSignedContractReady) {
                    buttons.push(getActionButton(<SaveIcon />, "Save",
                        () => {
                            setLoadingButton("Save");
                            setSubmitAction("SaveSignedContract")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                    buttons.push(getActionButton(<SendIcon />, "Save & Send",
                        () => {
                            setLoadingButton("Save & Send");
                            setSubmitAction("SendSignedContract")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                }

                break;

            case RequestStatus.WaitingForContract:
                if (isUserInRole(ApplicationRoles.AOFOpener)
                    && state.request.ownerKey === userInfo.externalKey) {
                    buttons.push(getActionButton(<SaveIcon />, "Save",
                        () => {
                            setLoadingButton("Save");
                            setSubmitAction("SaveSignedContract")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                    buttons.push(getActionButton(<SendIcon />, "Save & Send",
                        () => {
                            setLoadingButton("Save & Send");
                            setSubmitAction("SendSignedContract")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                }

                break;

            case RequestStatus.PricingUnit:
                if (isUserInRole(ApplicationRoles.PricingUnit)) {
                    buttons.push(
                        getActionButton(
                            <SaveIcon />,
                            "Save",
                            () => {
                                setLoadingButton("Save");
                                setSubmitAction("SavePricingInfo");
                                if (formRef.current)
                                    formRef.current.handleSubmit();
                            }
                        )
                    );

                    buttons.push(
                        getActionButton(
                            <SendIcon />,
                            "Save & Send",
                            () => {
                                setLoadingButton("Save & Send");
                                setSubmitAction("SendToARFromPricing");
                                if (formRef.current)
                                    formRef.current.handleSubmit();
                            }
                        )
                    );
                }

                break;
            
            case RequestStatus.AccountReceivableApproval:
                if (isUserInRole(ApplicationRoles.AccountReceivable)) {
                    buttons.push(getActionButton(<SaveIcon />, "Save",
                        () => {
                            setLoadingButton("Save");
                            setSubmitAction("SaveAccountRecInfo")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));

                    let relevantChanges = false;
                    
                    if (
                        state.request.dataChanges &&
                        state.userBusinessCategories[0] &&
                        (
                            state.userBusinessCategories[0].value === "JWA" ?
                            state.request.dataChanges.some(x => !x.targetProperty.startsWith("AccountReceivable.") && x.targetProperty !== "LogisticPlanning.CG4") :
                            state.request.dataChanges.some(x => !x.targetProperty.startsWith("AccountReceivable."))
                        )
                    )
                        relevantChanges = true;

                    if (relevantChanges) {
                        buttons.push(getActionButton(<SendIcon />, "Send to CRE",
                            () => {
                                setLoadingButton(null);
                                setNoteObligatory(true)
                                setSubmitAction("SendToCRE")
                                if (formRef.current)
                                    formRef.current.handleSubmit();
                            }));
                    }
                    else {
                        buttons.push(
                            getActionButton(
                                <SendIcon />,
                                "Send to SAP",
                                () => {
                                    setLoadingButton("Send to SAP");
                                    setSubmitAction("SendAccountRecInfo");

                                    if (formRef.current)
                                        formRef.current.handleSubmit();
                                }
                            )
                        );
                    }
                }

                break;

            case RequestStatus.WaitingToBeAddedInSap:
            case RequestStatus.Approved:
                if (isUserInRole(ApplicationRoles.AOFOpener)
                    && state.request.ownerKey === userInfo.externalKey
                    && !state.request.isSignedContractAvailable) {
                    buttons.push(getActionButton(<SendIcon />, "Save & Send",
                        () => {
                            setLoadingButton("Save & Send");
                            setSubmitAction("SendSignedContract")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                }

                break;

            case RequestStatus.SapError:
                if (isUserInRole(ApplicationRoles.Administrator)) {
                    buttons.push(getActionButton(<SendIcon />, "Resend to SAP",
                        () => {
                            setLoadingButton("Resend to SAP");
                            setSubmitAction("ResendToSAP")
                            if (formRef.current)
                                formRef.current.handleSubmit();
                        }));
                }

                break;

            case RequestStatus.Blocked:
                if (isUserInRole(ApplicationRoles.CreditRisk)){
                    buttons.push(
                        getActionButton(
                            <UnlockIcon />,
                            "Unblock",
                            () => {
                                setLoadingButton("Unblock");
                                setSubmitAction("Unblock");

                                if (formRef.current)
                                    formRef.current.handleSubmit();
                            }
                        )
                    );
                    buttons.push(
                        getActionButton(
                            <CancelIcon />,
                            "Close",
                            () => {
                                setLoadingButton("Close");
                                setSubmitAction("Close");
                                
                                if (formRef.current)
                                    formRef.current.handleSubmit();
                            }
                        )
                    );
                }

                break;

            default:
                break;
        }

        return buttons;
    }

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const getActionButton = (icon, text, action) => {
        if (isMobile) {
            return (
                <Grid item key={text}>
                    <Button
                        size="small"
                        type="button"
                        disabled={isSavingRequest}
                        className={classes.saveButton}
                        onClick={action}>
                        <Grid container>
                            <Grid item xs={12}>
                                {
                                    isSavingRequest && loadingButton === text ?
                                    <CircularProgress className={classes.progress} /> :
                                    icon
                                }
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="caption">
                                    {text}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Button>
                </Grid>
            );
        }
        else {
            return (
                <Grid item key={text}>
                    <Button
                        type="button"
                        variant="contained"
                        disabled={isSavingRequest}
                        className={classes.saveButton}
                        onClick={action}>
                        {
                            isSavingRequest && loadingButton === text ?
                            <><CircularProgress size={20} className={classes.progress} />&nbsp;&nbsp;{text}</> :
                            <>{icon}&nbsp;{text}</>
                        }
                    </Button>
                </Grid>
            );
        }
    }

    const submitClickHandler = async (values, actions) => {
        let success = false;

        switch (submitAction) {
            case "SaveDraft":
            case "SendDraft":
                if (!isNoteOpen && submitAction === "SendDraft"){
                    setIsNoteOpen(true);
                    return;
                }

                let isMandatoryPG = true;

                if (values.salesOrganizationId && state.userSalesOrgs.length > 0){
                    isMandatoryPG = !state.userSalesOrgs.some(x =>
                        values.salesOrganizationId === x.tableValueId &&
                        x.externalKey === "002P"
                    );
                }

                success = await saveDraftRequest(
                    {
                        ...state.request,
                        ...values,
                        customerRequestId: state.request.customerRequestId,
                        isMandatoryPG,
                        secondaryAddress: values.hasSecondaryAddress ? { ...values.secondaryAddress } : null,
                        order: {
                            ...values.order,
                            payer: values.order.isPayerDifferentFromClient ? { ...values.order.payer } : null,
                            billing: values.order.isBillingDifferentFromClient ? { ...values.order.billing } : null,
                        },
                        invoiceAddresses: values.isInvoceAddressDifferentFromCustomer ? [...values.invoiceAddresses] : null,
                        riskEvaluation: values.riskEvaluation ? { ...values.riskEvaluation } : null, //needed when send to review
                        ethicsCompliance: values.ethicsCompliance ? { ...values.ethicsCompliance } : null, //needed when send to review
                        creditManagement: values.creditManagement ? { ...values.creditManagement } : null
                    },
                    submitAction === "SendDraft"
                );

                if (success){
                    if (
                        submitAction === "SaveDraft" &&
                        state.request &&
                        state.request.customerRequestId === 0
                    )
                        setDraftRequests(draftRequests + 1);
                    else if (submitAction === "SendDraft" && draftRequests > 0)
                        setDraftRequests(draftRequests - 1);
                }

                break;

            case "SaveCreditRiskInfo":
            case "SendCreditRiskInfo":
                if (!isNoteOpen && submitAction === "SendCreditRiskInfo") {
                    setIsNoteOpen(true)
                    return
                }

                success = await updateCreditRiskInfo({
                    customerRequestId: values.customerRequestId,
                    paymentTermId: values.order.paymentTermId,
                    riskEvaluation: values.riskEvaluation ? { ...values.riskEvaluation } : null,
                    ethicsCompliance: values.ethicsCompliance ? { ...values.ethicsCompliance } : null,
                    creditManagement: values.creditManagement ? { ...values.creditManagement } : null,
                    note: values.note
                }, submitAction === "SendCreditRiskInfo");

                break;

            case "SaveLogisticInfo":
            case "SendLogisticInfo":
                if (!isNoteOpen && submitAction === "SendLogisticInfo") {
                    setIsNoteOpen(true)
                    return
                }

                const data = [];
                if (isUserInRole(ApplicationRoles.LogisticDocumentation) && !values.isPerfumesCategory) {
                    data.push({
                        type: 0,
                        customerRequestId: values.customerRequestId,
                        logisticDocumentation: values.logisticDocumentation ? { ...values.logisticDocumentation, note: values.note } : null,
                        shippingTransportationZones: values.isInvoceAddressDifferentFromCustomer ?
                            values.invoiceAddresses.map((x) => ({ transportationZoneId: x.transportationZoneId })) : [],                        
                    });
                }

                if (isUserInRole(ApplicationRoles.LogisticCustomerService)) {
                    data.push({
                        type: 1,
                        customerRequestId: values.customerRequestId,
                        logisticCustomerService: values.logisticCustomerService ? { ...values.logisticCustomerService, note: values.note } : null,
                        transportationZoneId: values.logisticDocumentation && values.isPerfumesCategory ? values.logisticDocumentation.transportationZoneId : null,                        
                        shippingTransportationZones: values.isPerfumesCategory ? (values.isInvoceAddressDifferentFromCustomer ?
                            values.invoiceAddresses.map((x) => ({ transportationZoneId: x.transportationZoneId })) : []) : [],                        
                    });
                }

                if (isUserInRoles([ApplicationRoles.LogisticPlanning, ApplicationRoles.LogisticPlanningPerfumes])) {
                    data.push({
                        type: 2,
                        customerRequestId: values.customerRequestId,
                        logisticPlanning: values.logisticPlanning ? { ...values.logisticPlanning, isPerfumesCategory: values.isPerfumesCategory, isAccountReceivable: RequestStatus.AccountReceivableApproval === values.status, note: values.note } : null,                        
                    });
                }

                success = await updateLogisticInfo(data, submitAction === "SendLogisticInfo");

                break;

            case "SaveSignedContract":
            case "SendSignedContract":
                if (!isNoteOpen && submitAction === "SendSignedContract") {
                    setIsNoteOpen(true)
                    return
                }

                // Add contract when Handled In Sap flag
                //
                // This flag is based on customer request status and determines if the user can set  the contract
                // availability. This is only used when AOF opener need to add the contract while the CR is handled by SAP
                const addContractWhenHIS = values.status === RequestStatus.WaitingToBeAddedInSap
                    || values.status === RequestStatus.Approved;

                success = await updateSignedContractInfo({
                    customerRequestId: values.customerRequestId,
                    isSignedContractAvailable: !addContractWhenHIS ? values.isSignedContractAvailable : true,
                    signedContract: values.signedContract ? { ...values.signedContract } : null,
                    note: values.note
                }, submitAction === "SendSignedContract");

                break;

            case "SaveAccountRecInfo":
            case "SendAccountRecInfo":
                if (!isNoteOpen && submitAction === "SendAccountRecInfo") {
                    setIsNoteOpen(true)
                    return
                }

                success = await updateAccountReceivableInfo({
                    ...state.request,
                    ...values,
                    secondaryAddress: values.hasSecondaryAddress ? values.secondaryAddress : null,
                    order: {
                        ...values.order,
                        payer: values.order.isPayerDifferentFromClient ? { ...values.order.payer } : null,
                        billing: values.order.isBillingDifferentFromClient ? { ...values.order.billing } : null,
                    },
                    invoiceAddresses: values.isInvoceAddressDifferentFromCustomer ? [...values.invoiceAddresses] : null,
                    logisticCustomerService: values.logisticCustomerService ? { ...values.logisticCustomerService, transportationZoneId: values.logisticDocumentation ? values.logisticDocumentation.transportationZoneId : null } : null,
                    logisticPlanning: values.logisticPlanning ? { ...values.logisticPlanning, isPerfumesCategory: values.isPerfumesCategory, isAccountReceivable: RequestStatus.AccountReceivableApproval === values.status } : null
                }, submitAction === "SendAccountRecInfo");

                break;

            case "SendToCreditRisk":
                if (!isNoteOpen) {
                    setIsNoteOpen(true)
                    return
                }

                success = await sendRequestToCreditRisk({ customerRequestId: values.customerRequestId, note: values.note, dbType: values.dbType });
                break;

            case "Block":
                if (!isNoteOpen) {
                    setIsNoteOpen(true);
                    return;
                }

                success = await blockRequest({
                    customerRequestId: values.customerRequestId,
                    paymentTermId: values.order.paymentTermId,
                    riskEvaluation: values.riskEvaluation ? { ...values.riskEvaluation } : null,
                    ethicsCompliance: values.ethicsCompliance ? { ...values.ethicsCompliance } : null,
                    creditManagement: values.creditManagement ? { ...values.creditManagement } : null,
                    note: values.note
                });

                break;

            case "Unblock":
                if (!isNoteOpen) {
                    setIsNoteOpen(true);
                    return;
                }

                success = await unblockRequest({
                    customerRequestId: values.customerRequestId,
                    note: values.note,
                    dbType: values.dbType
                });

                break;

            case "Close":
                if (!isNoteOpen) {
                    setIsNoteOpen(true);
                    return;
                }

                success = await closeRequest({
                    customerRequestId: values.customerRequestId,
                    note: values.note,
                    dbType: values.dbType
                });

                break;

            case "SendToLogistic":
                if (!isNoteOpen) {
                    setIsNoteOpen(true)
                    return
                }

                success = await sendRequestToLogistic({
                    customerRequestId: values.customerRequestId,
                    note: values.note,
                    dbType: values.dbType
                });

                break;

            case "SendToCRE":
                if (!isNoteOpen) {
                    setIsNoteOpen(true)
                    return
                }

                success = await sendRequestToCRE({
                    customerRequestId: values.customerRequestId,
                    note: values.note,
                    dbType: values.dbType
                });

                break;

            case "SavePricingInfo":
            case "SendToARFromPricing":
                if (!isNoteOpen && submitAction === "SendToARFromPricing") {
                    setIsNoteOpen(true);
                    return;
                }

                success = await updatePricingInfo(
                    {
                        customerRequestId: values.customerRequestId,
                        pricingListId: values.pricing?.pricingListId,
                        pricingGroupId: values.pricing?.pricingGroupId,
                        note: values.note
                    },
                    submitAction === "SendToARFromPricing"
                );
                break;
            
            case "SendToARFromCR":
                if (!isNoteOpen) {
                    setIsNoteOpen(true)
                    return
                }

                success = await sendRequestToARFromCR({
                    customerRequestId: values.customerRequestId,
                    note: values.note,
                    dbType: values.dbType
                });

                break;

            case "ResendToSAP":
                success = await reSendRequestToSap(values.customerRequestId, values.dbType);
                break;

            case "SendToReview":
                if (!isNoteOpen) {
                    setIsNoteOpen(true)
                    return
                }

                success = await sendRequestToReview({
                    customerRequestId: values.customerRequestId,
                    note: values.note,
                    dbType: values.dbType
                });

                break;

            case "Reject":
                if (!isNoteOpen) {
                    setIsNoteOpen(true)
                    return
                }

                success = await rejectRequest({
                    customerRequestId: values.customerRequestId,
                    note: values.note,
                    dbType: values.dbType
                });

                break;

            default:
                break;
        }

        actions.setSubmitting(false);

        if (success
            && (submitAction.startsWith("Send") || submitAction === "Reject")) {
            navigateTo(NavRoutes.RunningRequests);
        }
    }

    const alertCommandHandler = (command) => {
        switch (command) {
            case "ShowBusinessTab":
                setCurrentTab("business");
                break;

            case "ShowLogisticsTab":
                setCurrentTab("logistics");
                break;

            case "ShowContractTab":
                setCurrentTab("contract");
                break;

            case "ShowPricingTab":
                setCurrentTab("pricing");
                break;

            case "ShowAccRecTab":
                setCurrentTab("accountReceivable");
                break;

            default:
                break;
        }
    }

    const a11yProps = (index) => {
        return {
            id: `scrollable-auto-tab-${index}`,
            'aria-controls': `scrollable-auto-tabpanel-${index}`,
        };
    }

    useEffect(() => {
        clearError("Note")
    }, [isNoteOpen]);

    useEffect(() => {
        if (errors && errors.length > 0) {
            setIdentificationError(false);
            setBankError(false);
            setBusinessError(false);
            setLogisticsError(false);
            setAdministrativeError(false);
            setPricingError(false);
            setAccountReceivableError(false);

            let isIdentificationMissing = false;
            let isBankMissing = false;
            let isBusinessMissing = false;
            let isLogisticsMissing = false;
            let isAdministrativeMissing = false;
            let isPricingMissing = false;
            let isAccountReceivableMissing = false;

            errors.map(error => {
                let errorField = error.errorId;

                if (errorField.indexOf("primaryAddress") !== -1)
                    errorField = "primaryAddress";
                else if (errorField.indexOf("secondaryAddress") !== -1)
                    errorField = "secondaryAddress";
                else if (errorField.indexOf("contacts") !== -1)
                    errorField = "contacts";
                else if (errorField.indexOf("invoiceAddresses") !== -1)
                    errorField = "invoiceAddresses";
                else if (errorField.indexOf("order") !== -1)
                    errorField = "order";
                else if (errorField.indexOf("resaleMargins") !== -1)
                    errorField = "resaleMargins";
                else if (errorField.indexOf("accountReceivable") !== -1)
                    errorField = "accountReceivable";
                else if (errorField.indexOf("riskEvaluation") !== -1)
                    errorField = "riskEvaluation";
                else if (errorField.indexOf("creditManagement") !== -1)
                    errorField = "creditManagement";
                else if (errorField.indexOf("pricing.") !== -1) //S4 only
                    errorField = "pricing";

                switch (errorField) {
                    case "customerName":
                    case "briefPresentation":
                    case "primaryAddress":
                    case "secondaryAddress":
                    case "vatNumber":
                    case "taxNumber":
                    case "yearlySalesBudget":
                    case "customerCurrencyId":
                    case "contacts":
                    case "customerSapCode":
                    case "principalOperationCountryId":
                    case "chainId":
                        isIdentificationMissing = true;
                        break;
                    case "bankName":
                    case "bankIBAN":
                    case "swiftBic":
                        isBankMissing = true;
                        break;
                    case "salesGroupId":
                    case "responsibilityAreaId":
                    case "businessCategoryId":
                    case "companyId":
                    case "salesOrganizationId":
                    case "salesOfficeId":
                    case "customerGroupId":
                    case "customerGroup6Id":
                    case "customerGroup7Id":
                    case "customerGroup8Id":
                    case "customerGroup9Id":
                    case "customerGroup10Id":
                    case "distributionChannelId":
                    case "riskEvaluation":
                    case "ethicsCompliance.MitigateActions":
                    case "creditManagement":
                        isBusinessMissing = true;
                        break;
                    case "incotermsId":
                    case "incotermsLocation":
                        isLogisticsMissing = true;
                        break;
                    case "logisticPlanning.CG4Id":
                        if (state.userBusinessCategories[0].value === "Perfumes")
                            isLogisticsMissing = true;
                        else if (state.userBusinessCategories[0].value === "JWA")
                            isAccountReceivableMissing = true;
                        break;
                    case "invoiceCurrencyId":
                    case "order":
                    case "invoiceAddresses":
                        isAdministrativeMissing = true;
                        break;
                    case "resaleMargins":
                    case "pricingListId":
                    case "pricingGroupId":
                    case "pricing":
                        isPricingMissing = true;
                        break;
                    case "accountReceivable":
                        isAccountReceivableMissing = true;
                        break;
                    default:
                        break;
                }
            });

            if (isIdentificationMissing)
                setIdentificationError(true);
            if (isBankMissing)
                setBankError(true);
            if (isBusinessMissing)
                setBusinessError(true);
            if (isLogisticsMissing)
                setLogisticsError(true);
            if (isAdministrativeMissing)
                setAdministrativeError(true);
            if (isPricingMissing)
                setPricingError(true);
            if (isAccountReceivableMissing)
                setAccountReceivableError(true);
        }
    }, [errors]);

    const actionButtons = getActionButtons();
    const hasActionButtons = actionButtons.length > 0;

    return (
        <>
            <Grid className={classes.header} container justifyContent="space-between">
                <Grid item>
                    <RequestHeader />
                </Grid>
                <Grid item>
                    <Hidden mdDown>
                        <Grid container spacing={2}>
                            {actionButtons}
                        </Grid>
                    </Hidden>
                </Grid>
                <Grid item xl={12} md={12} xs={12}>
                    <RequestStepper />
                </Grid>
                <Grid item xs={12} xl={12} md={12}>
                    <Tabs
                        className={classes.tabs}
                        onChange={(event, value) => setCurrentTab(value)}
                        value={currentTab}
                        variant="scrollable">
                        {getTabs()}
                    </Tabs>
                    <Divider className={classes.divider} />
                </Grid>
            </Grid>
            <Container style={{ marginBottom: hasActionButtons && isMobile ? 60 : null }}>
                <Formik                    
                    initialValues={createInitialValues(state.request)}
                    innerRef={formRef}
                    onSubmit={submitClickHandler}
                >
                    {(props) => {
                        if (state.request.status === RequestStatus.CreditRiskEvaluation){
                            const ethicsCompliance = props.values.ethicsCompliance;
                            
                            if (ethicsCompliance.areRedFlags && !ethicsCompliance.isMitigated)
                                setIsToBlock(true);
                            else
                                setIsToBlock(false);
                        }

                        return (
                            <Form noValidate autoComplete="off">
                                <TabsProvider {...props}>
                                    <NoteForm
                                        disabled={isSavingRequest}
                                        isNoteObligatory={isNoteObligatory}
                                        isNoteOpen={isNoteOpen}
                                        setIsNoteOpen={(arg) => setIsNoteOpen(arg)}
                                        submitForm={props.submitForm}
                                    />

                                    <RequestAlerts onCommand={alertCommandHandler} />

                                    {
                                        isUserInRole(ApplicationRoles.BossApprover) &&
                                        state.request.status === RequestStatus.Draft &&
                                        state.request.customerRequestId === 0 &&
                                        <OpenerSelector />
                                    }

                                    <DataContainer>
                                        {
                                            currentTab === 'identification' &&
                                            <IdentificationProvider {...props}>
                                                <IdentificationTab />
                                            </IdentificationProvider>
                                        }

                                        {
                                            currentTab === 'bank' &&
                                            <BankInfoProvider {...props}>
                                                <BankInfoTab disabled={isSavingRequest} />
                                            </BankInfoProvider>
                                        }

                                        {
                                            currentTab === 'business' &&
                                            <BusinessInfoProvider {...props}>
                                                <BusinessInfoTab />
                                            </BusinessInfoProvider>
                                        }

                                        {
                                            currentTab === 'logistics' &&
                                            <LogisticsProvider {...props}>
                                                <LogisticsTab />
                                            </LogisticsProvider>
                                        }

                                        {
                                            currentTab === 'administrative' &&
                                            <AdministrativeProvider {...props}>
                                                <AdministrativeTab />
                                            </AdministrativeProvider>
                                        }

                                        {
                                            currentTab === 'pricing' &&
                                            <PricingProvider>
                                                <PricingTab />
                                            </PricingProvider>
                                        }

                                        {
                                            currentTab === 'contract' &&
                                            <ContractProvider>
                                                <ContractTab />
                                            </ContractProvider>
                                        }

                                        {
                                            currentTab === 'accountReceivable' &&
                                            <AccountReceivableProvider>
                                                <AccountReceivableTab />
                                            </AccountReceivableProvider>
                                        }
                                    </DataContainer>
                                </TabsProvider>
                            </Form>
                        );
                    }}
                </Formik>
            </Container>

            {
                hasActionButtons &&
                <Hidden lgUp>
                    <AppBar className={classes.appBar}>
                        <Toolbar>
                            <Grid justifyContent="space-around" container spacing={2}>
                                {getActionButtons()}
                            </Grid>
                        </Toolbar>
                    </AppBar>
                </Hidden>
            }
        </>
    );
}